import BaseClass from "../../util/BaseClass";
import Context from "../../context/Context";
import FieldNames from "../../aiim/datasets/FieldNames";
import StatPeopleUnassigned from "./StatPeopleUnassigned";
import StatUnitsUnassigned from "./StatUnitsUnassigned";
import UndoManager from "./transaction/UndoManager";
import * as aiimUtil from "../../aiim/util/aiimUtil";
import * as officePlanUtil from "./officePlanUtil";
import * as sourceUtil from "./sourceUtil";

export default class OfficePlan extends BaseClass {

  static Action_AssignmentsUpdated = "officeplan/Action_AssignmentsUpdated";

  static Action_PersonAssignedToUnit = "officeplan/Action_PersonAssignedToUnit";
  static Action_PersonUnassignedFromUnit = "officeplan/Action_PersonUnassignedFromUnit";
  static Action_UnitUnassigned = "officeplan/Action_UnitUnassigned";

  static Rdx_ActiveUnit = "officeplan/Rdx_ActiveUnit";

  static Rdx_ActiveUnitName = "officeplan/Rdx_ActiveUnitName";
  static Rdx_CanAssignPersonToActiveUnit = "officeplan/Rdx_CanAssignPersonToActiveUnit";

  static Task_AssignPersonToUnit = "officeplan/Task_AssignPersonToUnit";

  static Stat_PeopleUnassigned = "officeplan/Stat_PeopleUnassigned";
  static Stat_UnitsUnassigned = "officeplan/Stat_UnitsUnassigned";

  static TagFS = "IndoorsSpaceplannerFS";
  static TypeKeywordFS = "IndoorsSpaceplannerFS";
  static TypeKeywordParentPfx = "IndoorsParentID";

  static VersionedTagFS = "IndoorsSpaceplannerVFS";
  static VersionedTypeKeywordFS = "IndoorsSpaceplannerVFS";
  static VersionedTypeKeywordParentPfx = "IndoorsParentID";
  static VersionedTypeKeywordVersionPfx = "IndoorsSpaceplannerVID";

  static SpaceAssignmentTypes = {
    office: "office",
    hotdesk: "hotdesk",
    hotel: "hotel",
    none: "none",
    notAssignable: "not assignable"
  };

  areasTable;
  planPeopleSource;
  planUnitsSource;
  planServiceItem;
  supportInfo;

  issues;

  isVersioned;
  versionInfo;

  statPeopleUnassigned;
  statUnitsUnassigned;
  title;
  undoManager;
  wasOpened;

  static getActive() {
    return Context.instance.spaceplanner.activePlan;
  }

  constructor(props) {
    super(props);
    this.undoManager = new UndoManager();
  }

  destroy() {
    super.destroy();
    this.undoManager.destroy();
  }

  _init() {
    const promise = new Promise((resolve,reject) => {

      this.statPeopleUnassigned = new StatPeopleUnassigned({
        key: OfficePlan.Stat_PeopleUnassigned,
        sourceKey: "People",
        requeryOnPlanActions: [
          OfficePlan.Action_AssignmentsUpdated,
          OfficePlan.Action_PersonAssignedToUnit,
          OfficePlan.Action_PersonUnassignedFromUnit,
          OfficePlan.Action_UnitUnassigned
        ]
      });

      this.statUnitsUnassigned = new StatUnitsUnassigned({
        key: OfficePlan.Stat_UnitsUnassigned,
        sourceKey: "Units",
        requeryOnPlanActions: [
          OfficePlan.Action_AssignmentsUpdated,
          OfficePlan.Action_PersonAssignedToUnit,
          OfficePlan.Action_PersonUnassignedFromUnit,
          OfficePlan.Action_UnitUnassigned
        ]
      });

      resolve();
    });
    return promise;
  }

  getAreaName(areaId,areaType) {
    if (this.areasTable) return this.areasTable.getAreaName(areaId,areaType);
  }

  getMetaInfo() {

    const djLocale = Context.instance.lib.dojo.locale;
    const makeDate = v => {
      const d = new Date(v);
      const s = djLocale.format(d, {
        formatLength: "short"
      });
      return s;
    };

    const info = {
      title: this.title,
      created: null,
      modified: null,
      summary: null,
      owner: null
    };

    if (this.wasOpened && this.isVersioned && this.versionInfo) {
      const project = Context.instance.spaceplanner.planner.project;
      const versionManager = project.versionedInfo.versionManager;
      let vi = this.versionInfo;
      info.created = makeDate(vi.creationDate);
      info.modified = makeDate(vi.modifiedDate);
      info.summary = vi.description;
      info.owner = versionManager.getVersionOwner(vi);
    } else if (this.wasOpened && this.planServiceItem) {
      let item = this.planServiceItem;
      info.created = makeDate(item.created);
      info.modified = makeDate(item.modified);
      info.summary = item.snippet;
      info.owner = item.owner;
    }

    return info;
  }

  getStat(key) {
    if (key === OfficePlan.Stat_PeopleUnassigned) {
      return this.statPeopleUnassigned;
    } else if (key === OfficePlan.Stat_UnitsUnassigned) {
      return this.statUnitsUnassigned;
    }
  }

  hasIssues() {
    return this.issues && this.issues.length > 0;
  }

  isAreasTableValid() {
    return !!(this.areasTable && this.areasTable.table);
  }

  isPeopleLayerValid() {
    const source = officePlanUtil.getPeopleSource();
    return !!(source && source.layer2D);
  }

  isUnitsLayerValid() {
    const source = officePlanUtil.getUnitsSource();
    return !!(source && source.layer2D);
  }

  isValid() {
    return this.isPeopleLayerValid() && this.isUnitsLayerValid()  && !this.hasIssues();
  }

  queryHotDeskAreas() {
    if (this.areasTable) {
      return this.areasTable.queryHotDeskAreas();
    }
    return Promise.resolve([]);
  }

  queryHotelAreas() {
    if (this.areasTable) {
      return this.areasTable.queryHotelAreas();
    }
    return Promise.resolve([]);
  }

  refreshMetaInfo(){
    if (this.wasOpened && this.isVersioned && this.versionInfo){
      const versionGuid = this.versionInfo.versionGuid;
      const project = Context.instance.spaceplanner.planner.project;
      const versionManager = project.versionedInfo.versionManager;
      const task={};
      versionManager.readVersionInfos(task).then(result=>{
        if(result.data && versionGuid){
          for(let i=0;i<result.data.versions.length;i++){
            const resultVersionGuid = result.data.versions[i].versionGuid;
            if(resultVersionGuid=== versionGuid){
              this.versionInfo = result.data.versions[i];
              break;
            }
          }
        }
      }).catch(error=>{
        console.error(error);
      });
    }else if (this.wasOpened && this.planServiceItem) {
      let peopleLastModified, unitsLastModified, maxLastModifiedDate;
      const unitsLayer = sourceUtil.getUnitsSource();
      const peopleLayer = sourceUtil.getPeopleSource();
      const peoplePromise=sourceUtil.queryLastEditDate(peopleLayer).then(result=>{
        peopleLastModified=result;
      }).catch(error=>{
        console.log("Error checking max date", error);
      })
      const unitsPromise=sourceUtil.queryLastEditDate(unitsLayer).then(result=>{
        unitsLastModified=result;
      }).catch(error=>{
        console.log("Error checking max date", error);
      })
      Promise.all([peoplePromise, unitsPromise]).then(()=>{
        if(peopleLastModified && unitsLastModified){
          if(peopleLastModified === unitsLastModified)
            maxLastModifiedDate = unitsLastModified;
          else if(unitsLastModified> peopleLastModified)
            maxLastModifiedDate = unitsLastModified;
          else if(unitsLastModified< peopleLastModified)
            maxLastModifiedDate = peopleLastModified;
          this.planServiceItem.modified=maxLastModifiedDate;
        }
      }).catch(error=>{
        console.log("Error", error);
      })
    }
  }

  refreshStats() {
    if (this.wasOpened && !this.hasIssues()) {
      this.statPeopleUnassigned.query();
      this.statUnitsUnassigned.query();
    }
  }

  supportsComment() {
    return !!(
      this.supportInfo &&
      this.supportInfo.commentsLayerInfo
    );
  }

  supportsMoveDates() {
    let ok = false;
    const layer = sourceUtil.getPeopleLayer();
    if (layer) {
      ok = !!aiimUtil.findField(layer.fields,FieldNames.PEOPLE_MOVE_DATE);
    }
    return ok;
  }

  supportsReview() {
    if (this.supportInfo && this.supportInfo.serviceItem &&
        this.supportInfo.serviceItem.access === "private") {
      return false;
    }
    if (this.isVersioned && this.versionInfo && this.versionInfo.access === "private") {
      return false;
    }
    let privilegeToReview = false;
    const user = Context.instance.user;
    const isPlanOwner = user.isPlanOwner();
    const isAdmin = user.isAdmin()
    if (!isPlanOwner && !isAdmin) {
      if (this.supportInfo.reviewersTable) {
        const planId = Context.instance.spaceplanner.planId
        const planReviewers = this.supportInfo.reviewersTable.getReviewersByPlanId(planId)
        if (planReviewers && planReviewers.reviewers && planReviewers.reviewers.length > 0) {
          planReviewers.reviewers.forEach(r => {
            if (r.attributes.username === user.getUsername()) {
              privilegeToReview = true
            }
          })
        }
      }
    } else {
      privilegeToReview = true;
    }
    return !!(
      this.supportInfo &&
      this.supportInfo.reviewersTableInfo &&
      privilegeToReview
    );
  }

}
